import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';

import { IconClock } from '@tabler/icons-react';

interface ITimePickerProps {
  label?: string;
  value?: string | null;
  onChange: (time: string | null) => void;
  error?: string;
  isRequired?: boolean;
  disabled?: boolean;
  minTime?: string;
  placeholder?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) =>
  i.toString().padStart(2, '0')
);
const MINUTES = Array.from({ length: 12 }, (_, i) =>
  (i * 5).toString().padStart(2, '0')
);

const TimePicker: React.FC<ITimePickerProps> = ({
  label,
  value,
  onChange,
  error,
  isRequired,
  disabled,
  minTime,
  placeholder = 'Select time',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string>('');
  const [selectedMinute, setSelectedMinute] = useState<string>('');
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    openUpwards: false,
  });

  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  // Sync selected hour/minute from value prop
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setSelectedHour(h || '');
      setSelectedMinute(m || '');
    } else {
      setSelectedHour('');
      setSelectedMinute('');
    }
  }, [value]);

  // Calculate dropdown position
  useLayoutEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpwards = spaceBelow < 320 && spaceAbove > spaceBelow;
      const GAP = 8;

      setDropdownPosition({
        top: openUpwards ? rect.top - 4 - GAP : rect.bottom + 4 + GAP,
        left: rect.left,
        openUpwards,
      });
    }
  }, [isOpen]);

  // Scroll to selected hour/minute when opening
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        if (selectedHour && hourListRef.current) {
          const el = hourListRef.current.querySelector(
            `[data-hour="${selectedHour}"]`
          );
          el?.scrollIntoView({ block: 'center' });
        }
        if (selectedMinute && minuteListRef.current) {
          const el = minuteListRef.current.querySelector(
            `[data-minute="${selectedMinute}"]`
          );
          el?.scrollIntoView({ block: 'center' });
        }
      });
    }
  }, [isOpen, selectedHour, selectedMinute]);

  // Handle clicks outside and scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = (e: Event) => {
      if (
        dropdownRef.current &&
        e.target instanceof HTMLElement &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
    }
  }, [disabled]);

  const handleHourSelect = useCallback(
    (hour: string) => {
      if (minTime) {
        const [minH] = minTime.split(':');
        if (hour < minH) return;
      }
      setSelectedHour(hour);
      const minute = selectedMinute || '00';
      onChange(`${hour}:${minute}`);
    },
    [selectedMinute, onChange, minTime]
  );

  const handleMinuteSelect = useCallback(
    (minute: string) => {
      if (minTime) {
        const [minH, minM] = minTime.split(':');
        if (selectedHour < minH || (selectedHour === minH && minute < minM))
          return;
      }
      setSelectedMinute(minute);
      const hour = selectedHour || '00';
      onChange(`${hour}:${minute}`);
    },
    [selectedHour, onChange, minTime]
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setIsOpen(false);
  }, [onChange]);

  const handleNow = useCallback(() => {
    const now = new Date();
    const h = now.getHours().toString().padStart(2, '0');
    const m = (Math.round(now.getMinutes() / 5) * 5)
      .toString()
      .padStart(2, '0');
    onChange(`${h}:${m === '60' ? '00' : m}`);
    setIsOpen(false);
  }, [onChange]);

  // Format display value
  const displayValue = value || '';

  return (
    <div className="relative w-full">
      {label && (
        <label className="block text-sm font-medium text-neutral-primary leading-5 mb-1.5">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}

      <div
        ref={inputRef}
        className={`relative flex items-center justify-between w-full px-3 py-2.5 border rounded-lg transition-all cursor-pointer ${
          disabled
            ? 'border-neutral-200 bg-neutral-50 focus-within:outline-none cursor-not-allowed! '
            : error
              ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-100'
              : 'border-neutral-200 hover:border-brand-default focus-within:outline-2 focus-within:outline-offset-2 focus-within:border-[#e3ecff] focus-within:outline-[#e3ecff] bg-white '
        }`}
        onClick={handleInputClick}
      >
        <span
          className={`w-full bg-transparent outline-none text-sm font-medium ${
            disabled
              ? 'text-neutral-disabled cursor-not-allowed!'
              : displayValue
                ? 'text-neutral-900'
                : 'text-neutral-disabled'
          } cursor-pointer`}
        >
          {displayValue || placeholder}
        </span>
        <IconClock size={20} className="text-neutral-400" />
      </div>

      {error && (
        <p className="text-sm font-normal text-red-500 mt-1.5">{error}</p>
      )}

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="absolute z-9999 bg-white border border-neutral-200 rounded-xl shadow-2xl overflow-hidden"
            style={{
              top: dropdownPosition.openUpwards ? 'auto' : dropdownPosition.top,
              bottom: dropdownPosition.openUpwards
                ? window.innerHeight - dropdownPosition.top
                : 'auto',
              left: dropdownPosition.left,
              width: 220,
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between py-2 px-3 border-b border-neutral-200">
              <span className="text-xs font-semibold text-neutral-primary">
                Hour
              </span>
              <span className="text-xs font-semibold text-neutral-primary">
                Minute
              </span>
            </div>

            {/* Time selectors */}
            <div className="flex" style={{ height: 200 }}>
              {/* Hours column */}
              <div
                ref={hourListRef}
                className="flex-1 overflow-y-auto border-r border-neutral-200 py-1"
              >
                {HOURS.map((hour) => {
                  const [minH] = (minTime || '00:00').split(':');
                  const isDisabled = !!minTime && hour < minH;
                  return (
                    <button
                      key={hour}
                      data-hour={hour}
                      disabled={isDisabled}
                      onClick={() => handleHourSelect(hour)}
                      className={`w-full py-1.5 text-center text-xs font-medium transition ${
                        isDisabled
                          ? 'text-neutral-disabled cursor-not-allowed'
                          : selectedHour === hour
                            ? 'bg-brand-default text-white'
                            : 'hover:bg-neutral-100 text-neutral-primary'
                      }`}
                    >
                      {hour}
                    </button>
                  );
                })}
              </div>

              {/* Minutes column */}
              <div ref={minuteListRef} className="flex-1 overflow-y-auto py-1">
                {MINUTES.map((minute) => {
                  const [minH, minM] = (minTime || '00:00').split(':');
                  const isDisabled =
                    !!minTime &&
                    (selectedHour < minH ||
                      (selectedHour === minH && minute < minM));
                  return (
                    <button
                      key={minute}
                      data-minute={minute}
                      disabled={isDisabled}
                      onClick={() => handleMinuteSelect(minute)}
                      className={`w-full py-1.5 text-center text-xs font-medium transition ${
                        isDisabled
                          ? 'text-neutral-disabled cursor-not-allowed'
                          : selectedMinute === minute
                            ? 'bg-brand-default text-white'
                            : 'hover:bg-neutral-100 text-neutral-primary'
                      }`}
                    >
                      {minute}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-3 border-t border-neutral-200">
              <button
                onClick={handleClear}
                className="text-xs font-medium text-neutral-primary hover:text-brand-default"
              >
                Clear
              </button>
              <button
                onClick={handleNow}
                className="text-xs font-medium text-brand-default hover:text-brand-default/80"
              >
                Now
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TimePicker;
